import React, { useState } from 'react';
import * as Yup from 'yup';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPasswordSchema } from '../validations/SchemaValidations';
import { useForgetPasswordMutation } from '../redux/apiSlice';
import { Loader2 } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [forgetPassword, { isLoading }] = useForgetPasswordMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setFormErrors((prevErrors) => ({ ...prevErrors, email: '' })); // Clear email error on change
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await forgotPasswordSchema.validate({ email }, { abortEarly: false });
      setFormErrors({});
      await forgetPassword({ email: email.toLowerCase() }).unwrap(); // Unwrap to handle the promise
      toast.success(
        <div>
          <strong>Password Sent!</strong>
          <br />
          We've sent your password to <strong>{email}</strong>. Please check
          your inbox (and spam folder).
          <br />
          If you don't receive the email within a few minutes,
          <a
            href="#"
            className="underline text-blue-950"
            onClick={async (e) => {
              e.preventDefault();
              try {
                // Re-trigger the same API to resend the password email
                await forgetPassword({ email }).unwrap();
                toast.success('Password resend request sent!');
              } catch (error) {
                toast.error('Failed to resend password email');
              }
            }}
          >
            click here
          </a>{' '}
          to resend or contact our support team at{' '}
          <a href="mailto:info@MyCodeDragon.com">info@MyCodeDragon.com</a> for
          assistance.
        </div>,
      );

      navigate('/');
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        setFormErrors(errors);
      } else {
        toast.error('Failed to send password reset email');
      }
      console.error('Validation failed:', error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[85vh] px-4 py-6">
      <div className="bg-white p-4 sm:p-6 md:p-8 shadow-[0_0_15px_rgba(0,0,0,0.2)] w-full max-w-[638px] rounded-lg">
        <h1 className="text-xl sm:text-2xl font-bold mb-4">
          Forgot Your Password?
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="text-xs block font-medium">
              Email
            </label>
            <Input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleChange}
              placeholder="Enter your email"
              disabled={false}
            />
            {formErrors.email && (
              <p className="text-red-500 text-[13px]">{formErrors.email}</p>
            )}
          </div>

          <div className="flex justify-center">
            <Button
              type="submit"
              className="w-full sm:w-[80%] md:w-[60%] h-[45px] sm:h-[50px] flex items-center justify-center text-lg sm:text-xl md:text-2xl"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="animate-spin h-6 w-6" />
              ) : (
                'Send Me My Password'
              )}
            </Button>
          </div>
        </form>
        <div className="mt-4 text-center">
          <Link
            to="/login"
            className="text-[12px] text-[#858585] hover:underline"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
